import React from 'react';
import { NavLink } from 'react-router-dom';
import { 
  Home, 
  Search, 
  Layers, 
  Activity, 
  User,
  Settings,
  HelpCircle,
  Shield,
  Package,
  Heart,
  FolderOpen,
  Plus,
  ArrowLeftRight,
  Shirt,
  X
} from 'lucide-react';
import Logo3D from './Logo3D';

const MobileMenuDrawer = ({ isOpen, onClose }) => {
  const mainNavItems = [
    { path: '/', icon: Home, label: 'Home' },
    { path: '/explore', icon: Search, label: 'Explore' },
    { path: '/capsules', icon: Layers, label: 'Capsules' },
    { path: '/activity', icon: Activity, label: 'Activity' },
    { path: '/profile', icon: User, label: 'Profile' },
  ];

  const profileItems = [
    { path: '/portfolio', icon: FolderOpen, label: 'Portfolio' },
    { path: '/created', icon: Package, label: 'Created' },
    { path: '/favorites', icon: Heart, label: 'Favorites' },
    { path: '/listings', icon: Package, label: 'Listings' },
    { path: '/offers', icon: ArrowLeftRight, label: 'Offers' },
    { path: '/outfit-builder', icon: Shirt, label: 'Wardrobe' },
    { path: '/outfits', icon: Shirt, label: 'Outfits' },
    { path: '/trade-pieces', icon: ArrowLeftRight, label: 'Trade Pieces' },
  ];

  const createItems = [
    { path: '/create-nft', icon: Plus, label: 'Create Piece' },
    { path: '/create-collection', icon: Plus, label: 'Create Capsule' },
  ];

  const settingsItems = [
    { path: '/settings', icon: Settings, label: 'Settings' },
    { path: '/verification', icon: Shield, label: 'Verification' },
    { path: '/help', icon: HelpCircle, label: 'Help' },
  ];

  return (
    <>
      {/* Overlay */}
      <div 
        className={`md:hidden fixed inset-0 bg-black/80 z-40 transition-opacity duration-300 ${
          isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={onClose}
        style={{ backgroundColor: 'rgba(0, 0, 0, 0.8)' }}
      />

      {/* Drawer */}
      <div 
        className={`md:hidden fixed top-0 left-0 h-full w-[280px] bg-black border-r border-[#2a2a2a] z-50 transform transition-transform duration-300 ease-out ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        style={{ backgroundColor: '#000000' }}
      >
        {/* Header with Logo and Close Button */}
        <div className="flex items-center justify-between p-4 border-b border-[#2a2a2a] bg-black">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8">
              <Logo3D 
                className="w-full h-full"
                autoRotate={true}
                baseRotationSpeed={0.02}
                enableControls={false}
                showEnvironment={true}
                cameraPosition={[0, 0, 55]}
                fov={30}
                heroAreaMode={true}
                loadingMode={false}
              />
            </div>
            <span className="text-white font-semibold text-lg">Outsera</span>
          </div>
          <button 
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-[#2a2a2a] transition-colors"
          >
            <X size={20} className="text-white" />
          </button>
        </div>

        {/* Menu Content - Scrollable */}
        <div className="overflow-y-auto h-[calc(100%-73px)] scrollbar-thin bg-black">
          <nav className="p-4 space-y-6 bg-black">
            {/* Main Navigation */}
            <div>
              <h3 className="text-gray-400 text-xs font-semibold uppercase tracking-wider mb-3 px-2">
                Main
              </h3>
              <div className="space-y-1">
                {mainNavItems.map((item) => {
                  const Icon = item.icon;
                  return (
                    <NavLink
                      key={item.path}
                      to={item.path}
                      onClick={onClose}
                      className={({ isActive }) =>
                        `flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 ${
                          isActive 
                            ? 'bg-[#2a2a2a] text-white' 
                            : 'text-gray-400 hover:text-white hover:bg-[#1a1a1a]'
                        }`
                      }
                    >
                      <Icon size={20} />
                      <span className="font-medium">{item.label}</span>
                    </NavLink>
                  );
                })}
              </div>
            </div>

            {/* Profile Section */}
            <div>
              <h3 className="text-gray-400 text-xs font-semibold uppercase tracking-wider mb-3 px-2">
                Profile
              </h3>
              <div className="space-y-1">
                {profileItems.map((item) => {
                  const Icon = item.icon;
                  return (
                    <NavLink
                      key={item.path}
                      to={item.path}
                      onClick={onClose}
                      className={({ isActive }) =>
                        `flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 ${
                          isActive 
                            ? 'bg-[#2a2a2a] text-white' 
                            : 'text-gray-400 hover:text-white hover:bg-[#1a1a1a]'
                        }`
                      }
                    >
                      <Icon size={18} />
                      <span className="text-sm">{item.label}</span>
                    </NavLink>
                  );
                })}
              </div>
            </div>

            {/* Create Section */}
            <div>
              <h3 className="text-gray-400 text-xs font-semibold uppercase tracking-wider mb-3 px-2">
                Create
              </h3>
              <div className="space-y-1">
                {createItems.map((item) => {
                  const Icon = item.icon;
                  return (
                    <NavLink
                      key={item.path}
                      to={item.path}
                      onClick={onClose}
                      className={({ isActive }) =>
                        `flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 ${
                          isActive 
                            ? 'bg-[#2a2a2a] text-white' 
                            : 'text-gray-400 hover:text-white hover:bg-[#1a1a1a]'
                        }`
                      }
                    >
                      <Icon size={18} />
                      <span className="text-sm">{item.label}</span>
                    </NavLink>
                  );
                })}
              </div>
            </div>

            {/* Settings Section */}
            <div>
              <h3 className="text-gray-400 text-xs font-semibold uppercase tracking-wider mb-3 px-2">
                More
              </h3>
              <div className="space-y-1">
                {settingsItems.map((item) => {
                  const Icon = item.icon;
                  return (
                    <NavLink
                      key={item.path}
                      to={item.path}
                      onClick={onClose}
                      className={({ isActive }) =>
                        `flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 ${
                          isActive 
                            ? 'bg-[#2a2a2a] text-white' 
                            : 'text-gray-400 hover:text-white hover:bg-[#1a1a1a]'
                        }`
                      }
                    >
                      <Icon size={18} />
                      <span className="text-sm">{item.label}</span>
                    </NavLink>
                  );
                })}
              </div>
            </div>
          </nav>
        </div>
      </div>
    </>
  );
};

export default MobileMenuDrawer;

